import type { GetSkillFactorRes, SkillCollectionType, SkillItemType } from '@/types/skill'
import { formatNumber } from '@/utils'
import { levelClassMap, relicElementNameMap, relicIcon } from '@/views/item/data'
import { debuffCollection } from './debuff'
import { equipAffixCollection, explainEquipAffix } from '../equip/affix'
import type { ElementType } from '@/types/relic'
import type { SpecialKey } from '@/types/rune'
import { useUserStore } from '@/stores/modules/user'

export let skillCollection: SkillCollectionType = {}
export function setSkillData(data: SkillCollectionType) {
  skillCollection = data
}

export let skillSpecialFactor: Partial<Record<SpecialKey, any>> = {}
export function setSkillFactor(res: GetSkillFactorRes) {
  skillSpecialFactor = res.skillSpecialFactor
}

export const skillItemElementIcon: Record<ElementType, string> = {
  wind: '📗',
  fire: '📕',
  water: '📘',
  soil: '📙',
}
export function getSkillName(item: SkillItemType, noShowIcon = false, showLevelType = 1, showSpec = true) {
  const { skillId } = item
  const skillInfo = skillCollection[skillId]
  if (noShowIcon) return skillInfo.name

  const typeIcon = skillItemElementIcon[skillInfo.element[0]]
  const elementIcon = skillInfo.element.map((el) => relicIcon[el]).join('')

  let specName = ''
  if (showSpec) {
    const specKeyList = item.attrs?.filter((affix) => affix.special).map((item) => item.special!.key) || []
    const specArr: string[] = []
    specArr.push(...specKeyList.map((key) => equipAffixCollection[key].name))
    specName = `<span class="dark-gold-spec-info">[${specArr.join('] [')}]</span>`
  }

  const expSkillLevel = useUserStore().getSkillExpLevel(skillId)
  const realSkillLevel = useUserStore().getSkillRealLevel(skillId)
  const showSkillLevel = showLevelType === 1 ? realSkillLevel : expSkillLevel

  return `${typeIcon} ${skillInfo.name} ${elementIcon}${showLevelType ? `${showSkillLevel ? `[${showSkillLevel}]` : ''}` : ''} ${specName}`
}
export function getSkillNameById(id: number, noShowIcon = false) {
  const skillInfo = skillCollection[id]
  if (noShowIcon) return skillInfo.name
  const typeIcon = skillItemElementIcon[skillInfo.element[0]]
  const elementIcon = skillInfo.element.map((el) => relicIcon[el]).join('')
  return `${typeIcon} ${skillInfo.name} ${elementIcon}`
}
export const skillQualityDamageMag = {
  normal: 1,
  race: 1.1,
  epic: 1.2,
  legend: 1.3,
  antiquity: 1.5,
}
export function getSkillDesc(id: number, quality: keyof typeof skillQualityDamageMag) {
  const item = skillCollection[id]
  const effectDescList: string[] = []
  item.effect.forEach((eff) => {
    if (eff.type === 'damage') {
      let res = `造成当前攻击力 <span class="grow">${formatNumber(
        eff.value * 100 * skillQualityDamageMag[quality],
        2,
      )}%</span> 的<span class="info">${relicIcon[eff.element]}${relicElementNameMap[eff.element]}属性伤害</span>`
      if (eff.special && eff.special.length > 0) {
        res += `，${explainEquipAffix(eff.special, false, false)}`
      }
      effectDescList.push(res)
    } else if (eff.type === 'debuff') {
      effectDescList.push(
        `<span class="grow">${formatNumber(eff.rate, 2)}%</span> 概率向敌方施加持续 <span class="grow">${formatNumber(
          eff.duration / 1000,
          2,
        )}s</span>的<span class="info">${debuffCollection[eff.buffId]}</span>效果`,
      )
    }
  })
  return effectDescList.join('<br />')
}

export function getSkillAffixClass(val: number) {
  if (val < 2) return 'normal'
  else if (val >= 2 && val < 4) return 'race'
  else if (val >= 4 && val < 6) return 'epic'
  else if (val >= 6 && val < 8) return 'legend'
  else if (val >= 8 && val < 10) return 'antiquity'
  else return 'peerless'
}

export function explainSkillAffix(affixList: { key: SpecialKey; data: any }[], showExtra: boolean = true) {
  const userStore = useUserStore()
  const extraAttrs = showExtra ? userStore.getExtraAttrs : {}
  return affixList.map((item) => {
    const { key, data } = item
    const equipAffix = equipAffixCollection[key]
    const name = equipAffixCollection[key].name
    const affixData = getRealAffixData(key, data)
    const desc = equipAffix.desc({ ...affixData, ...extraAttrs[key], soulType: userStore.soulType })
    const specKeyList = Object.keys(skillSpecialFactor[key])
    const percent = (data[specKeyList[0]] || data[specKeyList[1]]) * 10
    return `${
      data.value === 10 || data.multiplier === 10 || data.rate === 10 ? '<span>★</span> ' : ''
    }<span class="special">${name}</span>（<span class="darkGold">${formatNumber(percent, 2)}%</span>）：${desc}`
  })
}
function getRealAffixData(key: SpecialKey, data: any) {
  const factor = skillSpecialFactor[key]
  const res = { ...data }
  Object.keys(res).forEach((item: string) => {
    if (factor[item]) {
      res[item] = (res[item] + 10) * factor[item]
      if (key === 'swiftness') res[item] += 1
    }
  })
  return res
}

export function getSkillNameText(skillIdList: number[] = []) {
  return skillIdList
    .map((item) => `【<span class="${levelClassMap[Number(item.toString().slice(0, 1))]}">${getSkillNameById(item)}</span>】`)
    .join('、')
}
